/* ============================================================
   StatYoyo — client-side helpers + static reference data
   Real stats come from the API; this file only holds image
   resolution, win-rate -> tier derivation, and static brawler
   facts (role / rarity) that the backend does not store.
   ============================================================ */

// Match the icon filenames in public/media/icons (name -> slug).
export function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export function brawlerImage(name) {
  return `${process.env.PUBLIC_URL}/media/icons/${slugify(name)}.webp`;
}

// Win rate -> tier ramp (S..D). The backend has no tier concept, so we bucket.
export function deriveTier(winRate) {
  const w = winRate == null ? 0 : parseFloat(winRate);
  if (w >= 56) return 'S';
  if (w >= 53) return 'A';
  if (w >= 50) return 'B';
  if (w >= 47) return 'C';
  return 'D';
}

export const TIER_RANK = { S: 5, A: 4, B: 3, C: 2, D: 1 };

export function pct(value, digits = 1) {
  if (value == null || Number.isNaN(parseFloat(value))) return '—';
  return `${parseFloat(value).toFixed(digits)}%`;
}

export function initial(name) {
  const s = String(name || '').trim();
  return s ? s[0].toUpperCase() : '?';
}

/* Safari-safe timestamp parsing. The API serialises Postgres timestamps as
   naive ISO ("2025-01-03T06:10:16" — stored UTC, no zone marker) and raw
   Snowflake/Postgres styles can use a space separator, which Safari rejects.
   Normalise to strict ISO-8601 UTC before new Date(). */
export function parseTs(s) {
  if (!s) return null;
  let iso = String(s).trim().replace(' ', 'T');
  // " Z" (space before zone) → "Z"; naive → assume UTC
  iso = iso.replace(/\s+Z$/, 'Z');
  if (!/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso)) iso += 'Z';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function timeAgo(s) {
  const d = parseTs(s);
  if (!d) return '';
  const mins = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
}

/* Static brawler facts — role + rarity are fixed attributes of the
   character, not match stats, so it's safe to bundle them. Sourced
   from the original design dataset; unknown brawlers degrade gracefully. */
export const BRAWLER_META = {
  spike:            { role: 'Damage',    rarity: 'Legendary' },
  kenji:            { role: 'Assassin',  rarity: 'Epic' },
  moe:              { role: 'Damage',    rarity: 'Legendary' },
  cordelius:        { role: 'Assassin',  rarity: 'Mythic' },
  fang:             { role: 'Assassin',  rarity: 'Chromatic' },
  stu:              { role: 'Assassin',  rarity: 'Epic' },
  chester:          { role: 'Damage',    rarity: 'Chromatic' },
  larry_and_lawrie: { role: 'Artillery', rarity: 'Epic' },
  angelo:           { role: 'Marksman',  rarity: 'Legendary' },
  clancy:           { role: 'Damage',    rarity: 'Epic' },
  juju:             { role: 'Damage',    rarity: 'Mythic' },
  piper:            { role: 'Marksman',  rarity: 'Super Rare' },
  tara:             { role: 'Damage',    rarity: 'Mythic' },
  gene:             { role: 'Support',   rarity: 'Mythic' },
  bea:              { role: 'Marksman',  rarity: 'Epic' },
  carl:             { role: 'Damage',    rarity: 'Super Rare' },
  melodie:          { role: 'Assassin',  rarity: 'Epic' },
  mico:             { role: 'Assassin',  rarity: 'Epic' },
  pam:              { role: 'Support',   rarity: 'Epic' },
  colt:             { role: 'Damage',    rarity: 'Common' },
  surge:            { role: 'Damage',    rarity: 'Chromatic' },
  bull:             { role: 'Tank',      rarity: 'Rare' },
  shelly:           { role: 'Damage',    rarity: 'Common' },
  mortis:           { role: 'Assassin',  rarity: 'Mythic' },
  doug:             { role: 'Support',   rarity: 'Epic' },
};

export function brawlerMeta(name) {
  return BRAWLER_META[slugify(name)] || null;
}

// "EL PRIMO" -> "El Primo" for page titles. Matches Python's str.title(),
// which the backend's link-preview page uses for the same strings.
export function titleCase(name) {
  return String(name || '').toLowerCase().replace(/(^|[^a-z])([a-z])/g, (_, a, b) => a + b.toUpperCase());
}
